import React, { useState, useEffect } from "react";
import { api, formatErr } from "../../lib/api";
import { useSettings } from "../../context/ThemeContext";
import { Check, Upload, X } from "lucide-react";

const THEMES = [
  { key: "theme_1", name: "Boutique Editorial", desc: "Cream / dorado · tipografía serif, elegante y atemporal.", swatch: ["#FDFBF7", "#0A0A0A", "#B8922E"] },
  { key: "theme_2", name: "Tropical Earth", desc: "Verde selva / coral · moderno, cálido y vibrante.", swatch: ["#F4F7F4", "#1A3626", "#E07A5F"] },
  { key: "theme_3", name: "Midnight Obsidian", desc: "Negro / oro · lujo boutique, atmósfera nocturna.", swatch: ["#0B0F19", "#D4AF37", "#0F5132"] },
];

export default function AdminSettings() {
  const { settings, reload } = useSettings();
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (settings) setForm({ ...settings, social: { ...(settings.social || {}) } });
  }, [settings]);

  if (!form) return <div className="p-10"><div className="bn-eyebrow">Cargando...</div></div>;

  const save = async (partial) => {
    setErr(""); setMsg("");
    try {
      await api.put("/settings/admin", partial);
      setMsg("Guardado ✓");
      await reload();
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setErr(formatErr(e));
    }
  };

  const saveAll = () => save({
    current_theme: form.current_theme,
    social: form.social,
    val_avatar: form.val_avatar,
    val_name: form.val_name,
    val_welcome_message: form.val_welcome_message,
    hero_title: form.hero_title,
    hero_subtitle: form.hero_subtitle,
    hero_image: form.hero_image,
    contact_email: form.contact_email,
    contact_phone: form.contact_phone,
    contact_whatsapp: form.contact_whatsapp,
  });

  const onAvatarUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await toBase64(f);
    setForm({ ...form, val_avatar: b64 });
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-settings">
      <div className="bn-eyebrow">Personalización</div>
      <h1 className="bn-heading text-3xl md:text-4xl mt-2">Ajustes del sitio</h1>

      {/* Theme selector */}
      <div className="mt-10">
        <h2 className="bn-heading text-2xl">Tema visual del sitio</h2>
        <p className="text-sm mt-2" style={{ color: "var(--bn-text-muted)" }}>Elige el estilo que verán tus clientes. Se aplica al instante en todo el sitio público.</p>
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {THEMES.map((t) => (
            <button
              key={t.key}
              onClick={() => { setForm({ ...form, current_theme: t.key }); save({ current_theme: t.key }); }}
              className="bn-card p-5 text-left relative"
              style={{ borderColor: form.current_theme === t.key ? "var(--bn-accent)" : "var(--bn-border)", borderWidth: form.current_theme === t.key ? 2 : 1 }}
              data-testid={`theme-${t.key}`}
            >
              {form.current_theme === t.key && (
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--bn-accent)", color: "var(--bn-accent-fg)" }}>
                  <Check size={14} />
                </div>
              )}
              <div className="flex gap-2">
                {t.swatch.map((c, i) => (
                  <div key={i} className="w-8 h-8 rounded-full" style={{ background: c, border: "1px solid rgba(0,0,0,0.1)" }} />
                ))}
              </div>
              <div className="bn-heading text-xl mt-4">{t.name}</div>
              <div className="text-xs mt-2" style={{ color: "var(--bn-text-muted)" }}>{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="mt-12">
        <h2 className="bn-heading text-2xl">Hero principal</h2>
        <div className="mt-4 space-y-4">
          <Field label="Foto de portada (hero)">
            <div className="flex items-start gap-4 flex-wrap">
              {form.hero_image && (
                <div className="relative w-40 h-28 overflow-hidden" style={{ borderRadius: "var(--bn-card-radius)", border: "1px solid var(--bn-border)" }}>
                  <img src={form.hero_image} alt="hero" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-[220px] space-y-2">
                <label className="bn-btn-ghost inline-flex items-center gap-2 cursor-pointer">
                  <Upload size={14} /> Subir desde galería
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) setForm({ ...form, hero_image: await toBase64(f) });
                  }} data-testid="hero-upload" />
                </label>
                <input className="bn-input" value={form.hero_image || ""} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} placeholder="o pega una URL https://..." data-testid="hero-image-url" />
              </div>
            </div>
          </Field>
          <Field label="Título (usa \n para salto de línea)">
            <textarea rows={2} className="bn-input" value={form.hero_title || ""} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} data-testid="hero-title-input" />
          </Field>
          <Field label="Subtítulo">
            <input className="bn-input" value={form.hero_subtitle || ""} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} data-testid="hero-subtitle-input" />
          </Field>
        </div>
      </div>

      {/* Val */}
      <div className="mt-12">
        <h2 className="bn-heading text-2xl">Asistente {form.val_name || "Val"}</h2>
        <div className="mt-4 grid md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="flex flex-col items-center gap-3">
            <img src={form.val_avatar || ""} alt={form.val_name || "Val"} className="w-32 h-32 rounded-full object-cover" style={{ border: "2px solid var(--bn-accent)" }} />
            <label className="bn-btn-ghost inline-flex items-center gap-2 cursor-pointer">
              <Upload size={14} /> Cambiar foto
              <input type="file" accept="image/*" className="hidden" onChange={onAvatarUpload} data-testid="val-upload-avatar" />
            </label>
          </div>
          <div className="space-y-4 flex-1">
            <Field label="Nombre del asistente">
              <input className="bn-input" value={form.val_name || ""} onChange={(e) => setForm({ ...form, val_name: e.target.value })} data-testid="val-name" />
            </Field>
            <Field label="Mensaje de bienvenida">
              <textarea rows={2} className="bn-input" value={form.val_welcome_message || ""} onChange={(e) => setForm({ ...form, val_welcome_message: e.target.value })} data-testid="val-welcome" />
            </Field>
            <Field label="URL de avatar (opcional)">
              <input className="bn-input" value={form.val_avatar || ""} onChange={(e) => setForm({ ...form, val_avatar: e.target.value })} />
            </Field>
          </div>
        </div>
      </div>

      {/* Social links */}
      <div className="mt-12">
        <h2 className="bn-heading text-2xl">Redes sociales (opcionales)</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {["instagram", "facebook", "tiktok", "youtube", "whatsapp"].map((k) => (
            <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1) + (k === "whatsapp" ? " (número)" : " (URL)")}>
              <input
                className="bn-input"
                value={form.social?.[k] || ""}
                onChange={(e) => setForm({ ...form, social: { ...form.social, [k]: e.target.value } })}
                placeholder={k === "whatsapp" ? "+52..." : "https://..."}
                data-testid={`social-${k}`}
              />
            </Field>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="mt-12">
        <h2 className="bn-heading text-2xl">Información de contacto</h2>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <Field label="Email">
            <input className="bn-input" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="contact-email" />
          </Field>
          <Field label="Teléfono">
            <input className="bn-input" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="contact-phone" />
          </Field>
          <Field label="WhatsApp">
            <input className="bn-input" value={form.contact_whatsapp || ""} onChange={(e) => setForm({ ...form, contact_whatsapp: e.target.value })} data-testid="contact-whatsapp" />
          </Field>
        </div>
      </div>

      {err && <div className="mt-6 text-sm p-3" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }}>{err}</div>}
      {msg && <div className="mt-6 text-sm p-3" style={{ background: "rgba(22,163,74,0.1)", color: "#16a34a" }}>{msg}</div>}

      <div className="mt-8 sticky bottom-6 flex justify-end">
        <button onClick={saveAll} className="bn-btn-primary" data-testid="save-settings-btn">Guardar todos los cambios</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="bn-eyebrow block mb-2">{label}</span>
      {children}
    </label>
  );
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
